/**
 * Contact Advisor Drawer
 * - Opens/closes the side drawer
 * - Exposes window.openContactAdvisor() so any button in the theme
 *   can trigger the drawer via: onclick="openContactAdvisor()"
 * - Reopens the drawer automatically if the form was just submitted
 *   (so the user sees the success/error message)
 * - ESC to close, click outside to close, focus trap for accessibility
 */
(function () {
  'use strict';

  function initContactAdvisor(root) {
    if (!root || root.dataset.caInitialized === 'true') return;
    root.dataset.caInitialized = 'true';

    const drawer = root.querySelector('[data-contact-advisor-drawer]');
    const overlay = root.querySelector('[data-contact-advisor-overlay]');
    const openers = root.querySelectorAll('[data-contact-advisor-open]');
    const closers = root.querySelectorAll('[data-contact-advisor-close]');

    if (!drawer || !overlay) return;

    let previouslyFocused = null;

    function open() {
      previouslyFocused = document.activeElement;
      overlay.hidden = false;
      // Force reflow so the transition runs
      // eslint-disable-next-line no-unused-expressions
      overlay.offsetHeight;
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('contact-advisor-open');

      // Focus the first focusable element in the drawer
      const focusable = drawer.querySelector(
        'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        setTimeout(() => focusable.focus(), 200);
      }
    }

    function close() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('contact-advisor-open');

      setTimeout(() => {
        overlay.hidden = true;
      }, 350);

      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    }

    openers.forEach((btn) => btn.addEventListener('click', open));
    closers.forEach((btn) => btn.addEventListener('click', close));
    overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    // Expose a global trigger so the button can live outside this section
    // (e.g. as a Custom Liquid block inside the main product section)
    window.openContactAdvisor = open;
    window.closeContactAdvisor = close;

    // Auto-open the drawer if the form was submitted and returned
    // with a success or error message (Shopify reloads the page).
    const hasFeedback = drawer.querySelector(
      '.contact-advisor__success, .contact-advisor__error'
    );
    if (hasFeedback) {
      // Open the quote accordion too so the message is visible
      const quoteAccordion = drawer.querySelector('[data-quote-accordion]');
      if (quoteAccordion) quoteAccordion.open = true;
      open();

      // Clean the URL so refresh doesn't repost
      if (
        drawer.querySelector('.contact-advisor__success') &&
        window.history.replaceState
      ) {
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }

  function initAll() {
    document
      .querySelectorAll('[data-contact-advisor]')
      .forEach(initContactAdvisor);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Support Shopify theme editor live updates
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-contact-advisor]');
    if (section) initContactAdvisor(section);
  });
})();